import axios from 'axios';
import {
  TableOrderingContext,
  TableInfo,
  TableMenuCategory,
  TableMenuItem,
  TableOrder,
  TableOrderStatus,
  TablePaymentRequest,
  TablePaymentResult,
  TableApiResponse
} from '../types/tableOrdering';

class TableOrderingService {
  private client: any;

  constructor() {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    
    this.client = axios.create({
      baseURL: apiUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    console.log('Table Ordering Service initialized:', {
      baseURL: apiUrl,
    });
  }

  /**
   * Credentials for the public POS endpoints: the booking-engine API key
   * when one is known, otherwise the table's QR code. QR cards printed
   * from the POS carry only the code (?code=), which the backend accepts
   * for that table and its restaurant.
   */
  private authParams(contextApiKey?: string, contextCode?: string): { api_key: string } | { code: string } {
    // The scanned link wins over anything stored or baked into the build
    // (a stale key from another hotel would resolve the wrong hotel).
    if (contextApiKey) return { api_key: contextApiKey };
    if (contextCode) return { code: contextCode };

    const storedCode = localStorage.getItem('table_ordering_code');
    if (storedCode) return { code: storedCode };
    const apiKey = localStorage.getItem('table_ordering_api_key')
      || process.env.REACT_APP_BOOKING_ENGINE_API_KEY;
    if (apiKey) return { api_key: apiKey };

    throw new Error('This table link is incomplete. Please scan the QR code on your table again or contact restaurant staff.');
  }

  /**
   * Get table information and restaurant details
   */
  async getTableInfo(context: TableOrderingContext): Promise<TableApiResponse<TableInfo>> {
    try {
      const auth = this.authParams(context.apiKey, context.code);
      
      const response = await this.client.get(
        `/api/pos/public/tables/${context.tableId}/info/`,
        {
          params: {
            ...auth,
            restaurant_id: context.restaurantId
          }
        }
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error fetching table info:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get menu categories and items for restaurant
   */
  async getMenuForTable(context: TableOrderingContext): Promise<TableApiResponse<TableMenuCategory[]>> {
    try {
      const auth = this.authParams(context.apiKey, context.code);
      
      const response = await this.client.get(
        `/api/pos/public/restaurants/${context.restaurantId}/menu/`,
        {
          params: {
            ...auth
          }
        }
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error fetching table menu:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get specific menu item details
   */
  async getMenuItem(itemId: string, restaurantId: string, apiKey?: string): Promise<TableApiResponse<TableMenuItem>> {
    try {
      const auth = this.authParams(apiKey);
      
      const response = await this.client.get(
        `/api/pos/public/menu-items/${itemId}/`,
        {
          params: {
            ...auth,
            restaurant_id: restaurantId
          }
        }
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error fetching menu item:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Place a table order (uses booking engine API key authentication)
   */
  async placeTableOrder(orderData: any, apiKey?: string): Promise<TableApiResponse<TableOrder>> {
    try {
      const auth = this.authParams(apiKey);
      
      const response = await this.client.post(
        '/api/pos/public/orders/', 
        orderData,
        {
          params: {
            ...auth
          }
        }
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error placing table order:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get order status by order number and phone (for tracking without auth)
   */
  async getOrderStatus(orderNumber: string, guestPhone: string, apiKey?: string): Promise<TableApiResponse<TableOrderStatus>> {
    try {
      const auth = this.authParams(apiKey);
      
      const response = await this.client.get(
        `/api/pos/public/orders/${orderNumber}/status/`,
        {
          params: {
            ...auth,
            phone: guestPhone
          }
        }
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error fetching order status:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get full order details by order number and phone
   */
  async getOrderDetails(orderNumber: string, guestPhone: string, apiKey?: string): Promise<TableApiResponse<TableOrder>> {
    try {
      const auth = this.authParams(apiKey);
      
      const response = await this.client.get(
        `/api/pos/public/orders/${orderNumber}/`,
        {
          params: {
            ...auth,
            phone: guestPhone
          }
        }
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error fetching order details:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Process payment for table order
   */
  async processPayment(paymentData: TablePaymentRequest): Promise<TableApiResponse<TablePaymentResult>> {
    try {
      const response = await this.client.post('/api/public/table-orders/payment/', paymentData);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error processing payment:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Cancel table order (within time limit)
   */
  async cancelOrder(orderNumber: string, guestPhone: string, apiKey?: string): Promise<TableApiResponse<{ message: string }>> {
    try {
      const auth = this.authParams(apiKey);
      
      const response = await this.client.patch(
        `/api/pos/public/orders/${orderNumber}/cancel/`,
        {
          phone: guestPhone
        },
        {
          params: {
            ...auth
          }
        }
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error canceling order:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Validate table and restaurant are available for ordering
   */
  async validateTableAccess(context: TableOrderingContext): Promise<TableApiResponse<{ valid: boolean; message?: string }>> {
    try {
      const auth = this.authParams(context.apiKey, context.code);
      
      const response = await this.client.get(
        `/api/pos/public/tables/${context.tableId}/validate/`,
        {
          params: {
            ...auth,
            restaurant_id: context.restaurantId
          }
        }
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error validating table access:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get restaurant hours and availability
   */
  async getRestaurantAvailability(restaurantId: string, apiKey?: string, code?: string): Promise<TableApiResponse<{ 
    is_open: boolean; 
    message: string; 
    hours?: string; 
    next_opening?: string;
  }>> {
    try {
      const auth = this.authParams(apiKey, code);
      
      const response = await this.client.get(
        `/api/pos/public/restaurants/${restaurantId}/availability/`,
        {
          params: {
            ...auth
          }
        }
      );
      
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      console.error('Error checking restaurant availability:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  private handleError(error: any): Error {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || 
                     error.response.data?.detail || 
                     error.response.data?.error ||
                     `HTTP ${error.response.status}: ${error.response.statusText}`;
      return new Error(message);
    } else if (error.request) {
      // Network error
      return new Error('Network error. Please check your connection and try again.');
    } else {
      // Other error
      return new Error(error.message || 'An unexpected error occurred.');
    }
  }

  /**
   * Generate table ordering URL for QR codes
   */
  static generateTableOrderingURL(
    baseURL: string, 
    hotelId: string, 
    restaurantId: string, 
    tableId: string
  ): string {
    const url = new URL('/ordering/table', baseURL);
    url.searchParams.set('hotel', hotelId);
    url.searchParams.set('restaurant', restaurantId);
    url.searchParams.set('table', tableId);
    url.searchParams.set('source', 'qr');
    
    return url.toString();
  }
}

// Export class and singleton instance
export { TableOrderingService };
export const tableOrderingService = new TableOrderingService();
export default tableOrderingService;